import {useTranslation} from "react-i18next";
import {Component, Suspense, useMemo} from "react";
import fetchData from "../../../api/fetch-suspense-wrapper/FetchSuspenseWrapper";
import {getResultSummary} from "../../../api/results/getResultSummary";
import {createDetail} from "../../../utils/DetailsUtils";
import SkeletonRectangle from "../../skeletons/SkeletonRectangle";
import {normalizeAggregate} from "./asyncTableColumns";
import {aggregateShortLabel} from "./asyncTableGrouping";

/**
 * Agregat kolumny w `rowIndex`-tym wierszu stopki. Wiersz na agregat - kolumna z jednym agregatem ma go tylko
 * w pierwszym wierszu (kolejne wiersze są dla kolumn z kilkoma agregatami); brak i NONE - pusta komórka.
 * @param {Object} header
 * @param {number} rowIndex
 * @returns {string|undefined}
 */
export function footerAggregate(header, rowIndex) {
    return normalizeAggregate(header.aggregate)[rowIndex];
}

function Cell({
    data,
    rows,
    aggregate,
    aggregateOptions,
    field,
    valueFormatter,
}) {
    const {t} = useTranslation();

    const pageAggregation = useMemo(() => {
        return createDetail(aggregate, aggregateOptions, field, [], rows, {
            onlyGlobal: true,
            formatter: valueFormatter,
        });
    }, [rows, aggregate, aggregateOptions, field, valueFormatter]);

    const dataToRender = useMemo(() => {
        let tmp = data;
        if (!isNaN(+data)) tmp = +data;
        if (valueFormatter) return valueFormatter(tmp);
        if (typeof tmp === "number") return +tmp.toFixed(2);
        return tmp;
    }, [data, valueFormatter]);

    // przy kilku agregatach (kolejne wiersze stopki) skrót mówi, który to agregat
    return (
        <td>
            <span className="async-table-group-aggregate-type">
                {aggregateShortLabel(aggregate, t)}
            </span>
            {pageAggregation} | ~{dataToRender}
        </td>
    );
}

function AsyncTableFooterResource({
    resource,
    shouldIndex,
    headers,
    rows,
    rowsCount,
}) {
    const data = resource?.read();

    const headerCounter = {};

    return new Array(rowsCount).fill(0).map((_, rowIndex) => (
        <tr key={rowIndex}>
            {shouldIndex && <td />}
            {headers.map((header, index) => {
                const aggregate = footerAggregate(header, rowIndex);
                if (!aggregate) return <td key={index} />;
                const headerIndex = headerCounter[header.field] ?? 0;
                headerCounter[header.field] = headerIndex + 1;
                return (
                    <Cell
                        key={index}
                        header={header}
                        data={data?.[header.field + "_" + headerIndex]}
                        rows={rows}
                        aggregate={aggregate}
                        aggregateOptions={header.aggregateOptions}
                        field={header.field}
                        valueFormatter={header.valueFormatter}
                    />
                );
            })}
        </tr>
    ));
}

function AsyncTableFooterSkeleton({headersCount, rowsCount}) {
    return new Array(rowsCount).fill(0).map((_, index) => (
        <tr className="loader" key={index}>
            <td colSpan={headersCount}>
                &nbsp;
                <SkeletonRectangle />
            </td>
        </tr>
    ));
}

class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = {hasError: false};
    }

    static getDerivedStateFromError(error) {
        return {hasError: true};
    }

    render() {
        const {children, headersCount, t} = this.props;
        if (this.state.hasError) {
            return (
                <tr>
                    <td colSpan={headersCount} className="text-center">
                        {t("IOT.updateDeviceFailure")}
                    </td>
                </tr>
            );
        }

        return children;
    }
}

function AsyncTableFooterRenderer({shouldIndex, headers, rows, id}) {
    const {t} = useTranslation();

    const resource = useMemo(() => {
        if (!id || id.length === 0) return null;
        const data = [];
        for (let header of headers) {
            for (let agg of normalizeAggregate(header.aggregate)) {
                data.push({
                    type: agg,
                    // średnia ważona liczy się z massKey
                    column: header.aggregateOptions?.massKey || header.field,
                    name: header.field,
                    weightColumn: header.aggregateOptions?.countKey,
                });
            }
        }
        return fetchData(getResultSummary(id, data));
    }, [id, headers]);

    const rowsCount = useMemo(() => {
        return Math.max(
            ...headers.map((item) => normalizeAggregate(item.aggregate).length)
        );
    }, [headers]);

    if (!resource)
        return (
            <tfoot>
                <AsyncTableFooterSkeleton
                    headersCount={
                        shouldIndex ? headers.length + 1 : headers.length
                    }
                    rowsCount={rowsCount}
                />
            </tfoot>
        );
    return (
        <tfoot>
            <ErrorBoundary
                headersCount={shouldIndex ? headers.length + 1 : headers.length}
                t={t}>
                <Suspense
                    fallback={
                        <AsyncTableFooterSkeleton
                            headersCount={
                                shouldIndex
                                    ? headers.length + 1
                                    : headers.length
                            }
                            rowsCount={rowsCount}
                        />
                    }>
                    <AsyncTableFooterResource
                        resource={resource}
                        headers={headers}
                        shouldIndex={shouldIndex}
                        rows={rows}
                        rowsCount={rowsCount}
                    />
                </Suspense>
            </ErrorBoundary>
        </tfoot>
    );
}

export default function AsyncTableFooter({headers, shouldIndex, rows, id}) {
    const hasFooter = useMemo(() => {
        return headers.some(
            (item) => normalizeAggregate(item.aggregate).length > 0
        );
    }, [headers]);

    if (!hasFooter) return null;
    return (
        <AsyncTableFooterRenderer
            headers={headers}
            shouldIndex={shouldIndex}
            id={id}
            rows={rows}
        />
    );
}
